import { useMemo, useState } from 'react';
import { Stack } from '@mui/material';

import { Button, AddIcon, Page } from '@occc-app/design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { CreateRetailerNetworkLocationGroups } from '@occc-app/App/PolicyBasedAccessControl/userActions';
import {
  RetailerNetworkLocationFragment,
  RetailerNetworkLocationGroupFragment,
} from '@occc/graphql';

import { StoreFilterBar } from './components/store-filter-bar.component';
import { StoreMapSection } from './components/store-map-section.component';
import { StoresFlatList } from './components/stores-flat-list.component';
import { StoresGroupedList } from './components/stores-grouped-list.component';
import { ConnectionStatus } from './retailer-network-my-stores-v2-page.types';
import { hasActiveFilterOrSearch } from './retailer-network-my-stores-v2-page.utils';

import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';
import { RetailerNetworkUserDetailsLocationsAndLocationGroups } from '../retailer-network-user-details-page/retailer-network-user-details-locations-and-groups/retailer-network-user-details-locations-and-groups.component';
import CreateLocationGroupAndAddLocationsDialog from '../retailer-network-locations-configuration/retailer-network-location-groups/create-location-group-and-add-locations-dialog/create-location-group-and-add-locations-dialog.container';

export type RetailerNetworkMyStoresV2PageProps = WithRetailerNetworkProps & {
  locationGroups: RetailerNetworkLocationGroupFragment[];
  isLocationGroupsLoading: boolean;
  mapMarkerLocations: RetailerNetworkLocationFragment[];
  mapBoundsLocations: RetailerNetworkLocationFragment[];
  selectedLocationUuid: string | null;
  onPinClick: (locationUuid: string) => void;
  stateFilter: string;
  cityFilter: string;
  connectionStatusFilter: ConnectionStatus | '';
  search: string;
  stateOptions: string[];
  cityOptions: string[];
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onConnectionStatusChange: (value: ConnectionStatus | '') => void;
  onSearchChange: (value: string) => void;
  onReset: () => void;
};

const PAGE_TITLE = 'My Stores';
const BREADCRUMB_LINKS = [{ label: PAGE_TITLE }];

export const RetailerNetworkMyStoresV2PageComponent = ({
  retailerNetwork,
  locationGroups,
  isLocationGroupsLoading,
  mapMarkerLocations,
  mapBoundsLocations,
  selectedLocationUuid,
  onPinClick,
  stateFilter,
  cityFilter,
  connectionStatusFilter,
  search,
  stateOptions,
  cityOptions,
  onStateChange,
  onCityChange,
  onConnectionStatusChange,
  onSearchChange,
  onReset,
}: RetailerNetworkMyStoresV2PageProps) => {
  useBreadcrumbLinks(BREADCRUMB_LINKS);

  const [isCreateLocationGroupDialogOpen, setIsCreateLocationGroupDialogOpen] = useState(false);

  const {
    isRetailerNetworkSiteUserRole,
    retailerNetworkUserUuid,
    retailerNetworkUserRole,
    retailerNetworkUserOpenCommerceUserUuid,
  } = useRetailerNetworkUserSelector();

  const siteUserContent = useMemo(() => {
    if (
      !retailerNetwork.uuid ||
      !retailerNetworkUserUuid ||
      !retailerNetworkUserRole ||
      !retailerNetworkUserOpenCommerceUserUuid
    ) {
      return null;
    }
    return (
      <RetailerNetworkUserDetailsLocationsAndLocationGroups
        retailerNetworkUuid={retailerNetwork.uuid}
        retailerNetworkUserUuid={retailerNetworkUserUuid}
        retailerNetworkUserRole={retailerNetworkUserRole}
        openCommerceUserUuid={retailerNetworkUserOpenCommerceUserUuid}
      />
    );
  }, [
    retailerNetwork.uuid,
    retailerNetworkUserUuid,
    retailerNetworkUserRole,
    retailerNetworkUserOpenCommerceUserUuid,
  ]);

  return (
    <>
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          titleTestId: 'rc-my-stores-v2-title',
          subtitle: isRetailerNetworkSiteUserRole
            ? ''
            : `You currently manage ${retailerNetwork.numberOfLocations} stores.`,
        }}
        containerProps={{ variant: 'md' }}
        actions={
          <Can I={CreateRetailerNetworkLocationGroups({ uuid: retailerNetwork.uuid })}>
            <Button
              variant='contained'
              color='secondary'
              startIcon={<AddIcon />}
              onClick={() => setIsCreateLocationGroupDialogOpen(true)}
              role='button'
            >
              Create Location Group
            </Button>
          </Can>
        }
      >
        {isRetailerNetworkSiteUserRole ? (
          siteUserContent
        ) : (
          <Stack spacing={1} mt={2}>
            {retailerNetwork.numberOfLocations > 1 && (
              <StoreMapSection
                boundsLocations={mapBoundsLocations}
                pinLocations={mapMarkerLocations}
                selectedLocationUuid={selectedLocationUuid}
                onPinClick={onPinClick}
              />
            )}
            <Stack spacing={1}>
              <StoreFilterBar
                stateFilter={stateFilter}
                cityFilter={cityFilter}
                connectionStatusFilter={connectionStatusFilter}
                search={search}
                stateOptions={stateOptions}
                cityOptions={cityOptions}
                onStateChange={onStateChange}
                onCityChange={onCityChange}
                onConnectionStatusChange={onConnectionStatusChange}
                onSearchChange={onSearchChange}
                onReset={onReset}
              />
              {hasActiveFilterOrSearch({
                stateFilter,
                cityFilter,
                connectionStatusFilter,
                search,
              }) ? (
                <StoresFlatList
                  networkId={retailerNetwork.uuid}
                  stateFilter={stateFilter}
                  cityFilter={cityFilter}
                  connectionStatusFilter={connectionStatusFilter}
                  search={search}
                />
              ) : (
                <StoresGroupedList
                  locationGroups={locationGroups}
                  isLoading={isLocationGroupsLoading}
                  networkId={retailerNetwork.uuid}
                  stateFilter={stateFilter}
                  cityFilter={cityFilter}
                  connectionStatusFilter={connectionStatusFilter}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Page>
      {isCreateLocationGroupDialogOpen && (
        <CreateLocationGroupAndAddLocationsDialog
          retailerNetworkUuid={retailerNetwork.uuid}
          open={isCreateLocationGroupDialogOpen}
          onClose={() => setIsCreateLocationGroupDialogOpen(false)}
        />
      )}
    </>
  );
};

export const RetailerNetworkMyStoresV2Page = withRetailerNetwork(
  RetailerNetworkMyStoresV2PageComponent,
);
